'use client';

import { cn } from '@zealot/ui/lib/utils';
import * as React from 'react';

import type { FileUploadFile } from './file-upload';
import { useFileUploadContext } from './file-upload';

// gloss
type FileUploadImagePreviewProps = React.ComponentProps<'div'> & {
  file: FileUploadFile;
  alt: string;
};

// gloss
const FileUploadImagePreview = ({ file, alt, className, children, ...props }: FileUploadImagePreviewProps) => {
  React.useEffect(() => {
    const url = file.previewUrl;
    return () => {
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [file.previewUrl]);

  return (
    <div
      data-slot="file-upload-image-preview"
      className={cn(
        'relative flex size-full flex-col items-center justify-center gap-3 overflow-hidden rounded-lg',
        className,
      )}
      {...props}
    >
      {file.previewUrl && (
        <img src={file.previewUrl} alt={alt} className="absolute inset-0 size-full object-cover" aria-hidden="true" />
      )}
      {}
      <div className="absolute inset-0 rounded-lg bg-black/40" />
      {}
      <div className="relative z-10 flex flex-col items-center gap-2">{children}</div>
    </div>
  );
};

// gloss
type FileUploadImagePreviewDeleteTriggerProps = React.ComponentProps<'button'> & {
  file: FileUploadFile;
};

const FileUploadImagePreviewDeleteTrigger = ({
  file,
  className,
  type = 'button',
  children,
  ...props
}: FileUploadImagePreviewDeleteTriggerProps) => {
  const { files, onFilesChange, disabled } = useFileUploadContext();

  const handleClick = React.useCallback(() => {
    onFilesChange(files.filter((f) => f.id !== file.id));
  }, [files, onFilesChange, file.id]);

  return (
    <button
      data-slot="file-upload-image-preview-delete-trigger"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      aria-label={`Remove ${file.file.name}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium text-xs',
        'bg-destructive/80 text-destructive-foreground hover:bg-destructive',
        'disabled:pointer-events-none disabled:opacity-50',
        'transition-colors',
        className,
      )}
      {...props}
    >
      {children ?? 'Remove'}
    </button>
  );
};

export { FileUploadImagePreview, FileUploadImagePreviewDeleteTrigger };
